import { MACHINE_ERA } from './game.js';
import { chapterSpec, phaseOrder, stepFrom } from './schedule.js';
import type { Step } from './schedule.js';
import { COURSES } from './look.js';
import { gamesProgress } from './charge.js';
import type { ChargeState } from './charge.js';
import { stopOf } from './stop.js';
import { tablePlayers } from './tablegame.js';
import type { TableGameKind } from './tablegame.js';
import type { Chapter, Phase, Seat, ShowState, StopRun, StopStep } from './types.js';

/**
 * The whole evening on the clicker (Marek, 2026-09-25: "like a PowerPoint presentation where you
 * just click forward and backward"; the Mac's console and the iPad are the fallback, never needed).
 *
 * Every era's beats are slides (`stepFrom`), and two more kinds of slide sit between them:
 *
 *   - the table game: pass it on in 2617 after the pour, plant in 2791 after the plate. NEXT
 *     starts it; NEXT while it runs ends it and moves on to the stations. With nobody at the
 *     table to play it (a rehearsal with no phones), NEXT goes straight on: a game the room would
 *     refuse must never hold the clicker (Marek, 2026-09-26: "the clicker is not working anymore").
 *   - the stop's own steps once its games are done (stop.ts): each alarm, in 2213 the machines, the
 *     meeting, the vote, the verdict, the reveal and the scores — one NEXT each (`stopStep`, Marek
 *     2026-10-09: "full control over the evening with the clicker, no automatic triggers"). An alarm
 *     still on ends at the press, and the vote is counted at it.
 *
 * The games are the charge (Marek, 2026-09-28): there is no charge slide. A NEXT that would end the
 * games unfinished has to be pressed twice (`skippedGames`). Nothing moves the show on by itself.
 *
 * BACK never replays a game or a film and never undoes a vote.
 */

/** The table game an era plays, and the beat it follows. */
export const TABLE_GAME_OF: Partial<Record<Chapter, { kind: TableGameKind; after: Phase }>> = {
  // 2617 has no plate: pass it on follows the pour. 2791 ends on the reindeer (back with the last
  // pour, Marek 2026-09-27): plant follows the plate, so the kitchen is not kept waiting.
  4: { kind: 'pass', after: 'serve' },
  5: { kind: 'plant', after: 'course' },
};

export type Click =
  | Step
  | { kind: 'tableGame'; game: TableGameKind }
  /** Stop the table game still running, then take `then`. */
  | { kind: 'endTableGame'; then: Step | null }
  /** From before the stop met by itself: the captain's meeting about one guest. No longer offered. */
  | { kind: 'meeting'; suspect: number }
  | { kind: 'countVotes' }
  /** The stop's next step at once (stop.ts); pressed while games are still on, it ends them. */
  | { kind: 'stopStep' };

export interface ClickContext {
  chapter: Chapter;
  phase: Phase;
  /** A table game is on the table right now. */
  tableGameRunning: boolean;
  /** This era's table game has been played (it may have run out by itself), or tried. */
  tableGamePlayed: boolean;
  /** Someone at the table could play it: a seat with a phone in and a role. */
  tableGameCanStart: boolean;
  meetingOpen: boolean;
  /** A meeting has been held this era, called by a guest or the captain. */
  meetingHeld: boolean;
  /** Who a captain's meeting would be about now (`mostTraced`); null: no meeting to call. */
  suspect: number | null;
  /**
   * Where this era's stop is once its games are done (stop.ts), or null before. A room from before the
   * stop's steps says `undefined`, and the clicker jumps after the games as it did.
   */
  stop?: StopStep | null | undefined;
  /** Every phone has its Wrapped open: the evening's last step is taken. */
  wrapped?: boolean;
}

/** What one press of the clicker does from here; null when there is nothing to do. */
export function clickFrom(ctx: ClickContext, direction: 'next' | 'back'): Click | null {
  const { chapter, phase } = ctx;
  if (direction === 'next' && phase === 'farewell' && ctx.wrapped) return null;
  if (direction === 'back') {
    // Back out of a table game still running: it stops, and the show stays where it is.
    if (ctx.tableGameRunning) return { kind: 'endTableGame', then: null };
    return stepFrom(chapter, phase, 'back');
  }
  // The stop's own vote is counted by its step; an old yes-or-no meeting by counting it.
  if (ctx.meetingOpen && !(phase === 'fieldwork' && (ctx.stop === 'vote' || ctx.stop === 'meeting'))) return { kind: 'countVotes' };
  const table = TABLE_GAME_OF[chapter];
  if (table && phase === table.after) {
    if (ctx.tableGameRunning) return { kind: 'endTableGame', then: stepFrom(chapter, phase, 'next') };
    if (!ctx.tableGamePlayed && ctx.tableGameCanStart) return { kind: 'tableGame', game: table.kind };
  }
  if (phase === 'fieldwork' && ctx.stop !== undefined && ctx.stop !== 'done') return { kind: 'stopStep' };
  return stepFrom(chapter, phase, 'next');
}

/**
 * The guest a captain's meeting is about when the clicker calls it: the one named in the most of
 * this era's alarm traces. Only what every phone and the TV already show is used — each alarm's
 * named stretch of table — never who really raised it. Ties go to the one the latest alarm named,
 * then the lower seat. Null with no alarm this era, or nobody named who can still be voted on.
 */
/** What the clicker reads of the room: a console's own trimmed seats will do. */
type ClickState = Pick<ShowState, 'chapter' | 'phase' | 'game' | 'tableGame'> & {
  wrappedAt?: number | null;
  seats: readonly Pick<Seat, 'seat' | 'callsign' | 'profession' | 'brig'>[];
};

export function mostTraced(state: Pick<ClickState, 'chapter' | 'seats' | 'game'>): number | null {
  const alarms = state.game.anomalies.filter((a) => a.chapter === state.chapter).sort((a, b) => a.at - b.at);
  if (alarms.length === 0) return null;
  const votable = new Set(state.seats.filter((s) => s.callsign !== null && s.profession !== null && !s.brig).map((s) => s.seat));
  const named = new Map<number, { count: number; last: number }>();
  alarms.forEach((alarm, i) => {
    for (let seat = alarm.near[0]; seat <= alarm.near[1]; seat++) {
      if (!votable.has(seat)) continue;
      const was = named.get(seat) ?? { count: 0, last: -1 };
      named.set(seat, { count: was.count + 1, last: i });
    }
  });
  let best: number | null = null;
  for (const [seat, n] of named) {
    const top = best === null ? null : named.get(best)!;
    if (!top || n.count > top.count || (n.count === top.count && (n.last > top.last || (n.last === top.last && seat < best!)))) {
      best = seat;
    }
  }
  return best;
}

/** The clicker's context from the room's state, with what the Mac remembers of this era's table game. */
export function clickContext(
  state: ClickState,
  now: number,
  tableGamePlayed: boolean,
): ClickContext {
  const running = state.tableGame !== null && now < state.tableGame.endsAt;
  return {
    chapter: state.chapter,
    phase: state.phase,
    tableGameRunning: running,
    tableGamePlayed: tableGamePlayed || (state.tableGame !== null && state.tableGame.chapter === state.chapter),
    // The room's own rule, which every console can read: someone aboard to play (`tablePlayers`).
    tableGameCanStart: tablePlayers(state.seats).length > 0,
    meetingOpen: state.game.meeting !== null,
    meetingHeld: state.game.meetings.some((m) => m.chapter === state.chapter),
    suspect: mostTraced(state),
    stop: 'stop' in state.game ? (stopOf(state)?.step ?? null) : undefined,
    wrapped: Boolean(state.wrappedAt),
  };
}

/** Where the stop is, as one string: a NEXT that took a step has landed when this changes. */
export function stopKey(state: { chapter: number; game: { stop?: StopRun | null; meeting?: { id: string } | null } }): string {
  const stop = stopOf(state);
  return stop ? `${stop.chapter}:${stop.step}:${stop.fired}:${state.game.meeting?.id ?? ''}` : `${state.chapter}:-`;
}

/**
 * The whole evening as the clicker walks it, one piece per place the show can sit between two
 * presses (Marek, 2026-09-27: the console's bar "split by every time I click the clicker", and
 * what the next click is going to do). Each era opens with its arrival — the jump and the era's
 * film, which move on by themselves — then a piece for every pour, plate, photo, table game and
 * the games, which charge the ship; the evening ends docked, on The Gift.
 */
export interface EveningSlide {
  chapter: Chapter;
  kind: 'boarding' | 'arrive' | 'phase' | 'table' | 'stop' | 'dock' | 'wrapped';
  phase: Phase;
  label: string;
  /** A stop's own step, after its games (`kind: 'stop'`). */
  step?: StopStep;
}

/** A stop's steps as the bar shows them: one piece for all its alarms, which only the Mac counts. */
export function stopSlides(chapter: Chapter): { step: StopStep; label: string }[] {
  return [
    { step: 'alarms', label: 'alarms' },
    ...(chapter === MACHINE_ERA ? [{ step: 'machines' as const, label: 'machines' }] : []),
    { step: 'meeting', label: 'meeting' },
    { step: 'vote', label: 'vote' },
    { step: 'verdict', label: 'verdict' },
    { step: 'reveal', label: 'reveal' },
    { step: 'done', label: 'scores' },
  ];
}

function slideWord(chapter: Chapter, phase: Phase): string {
  if (phase === 'serve') return `pour ${chapter}`;
  if (phase === 'role') return 'role';
  if (phase === 'signal') return 'signal';
  if (phase === 'course' && (chapter === 1 || chapter === 3 || chapter === 5)) return `plate ${COURSES[chapter].index}`;
  if (phase === 'fieldwork') return 'games';
  return phase;
}

export function eveningSlides(): readonly EveningSlide[] {
  const out: EveningSlide[] = [{ chapter: 0, kind: 'boarding', phase: 'boarding', label: 'boarding' }];
  for (const chapter of [1, 2, 3, 4, 5] as const) {
    out.push({ chapter, kind: 'arrive', phase: 'jump', label: chapterSpec(chapter).era });
    const table = TABLE_GAME_OF[chapter];
    for (const phase of phaseOrder(chapter)) {
      if (phase === 'jump' || phase === 'intro' || phase === 'farewell') continue;
      out.push({ chapter, kind: 'phase', phase, label: slideWord(chapter, phase) });
      if (table?.after === phase) out.push({ chapter, kind: 'table', phase, label: table.kind === 'pass' ? 'pass it on' : 'plant' });
      if (phase === 'fieldwork') for (const s of stopSlides(chapter)) out.push({ chapter, kind: 'stop', phase, label: s.label, step: s.step });
    }
  }
  out.push({ chapter: 5, kind: 'dock', phase: 'farewell', label: 'the gift' });
  out.push({ chapter: 5, kind: 'wrapped', phase: 'farewell', label: 'wrapped' });
  return out;
}

/** The piece the room is on now. `stop` is this era's stop step, once its games are done. */
export function slideNow(chapter: Chapter, phase: Phase, tableGameRunning: boolean, stop?: StopStep | null, wrapped = false): number {
  const slides = eveningSlides();
  if (chapter === 0) return 0;
  if (phase === 'farewell') return wrapped ? slides.length - 1 : slides.length - 2;
  if (phase === 'jump' || phase === 'intro') return slides.findIndex((s) => s.chapter === chapter && s.kind === 'arrive');
  if (tableGameRunning && TABLE_GAME_OF[chapter]?.after === phase) {
    return slides.findIndex((s) => s.chapter === chapter && s.kind === 'table');
  }
  if (phase === 'fieldwork' && stop && stop !== 'charged') {
    const i = slides.findIndex((s) => s.chapter === chapter && s.kind === 'stop' && s.step === stop);
    if (i >= 0) return i;
  }
  return slides.findIndex((s) => s.chapter === chapter && s.kind === 'phase' && s.phase === phase);
}

/**
 * The piece a click lands on; null for one that stays where it is or nothing ahead. A stop's step needs
 * the stop (`stop`): another of its alarms stays on the alarms' piece.
 */
export function slideAfter(
  chapter: Chapter,
  click: Click | null,
  stop?: Pick<StopRun, 'step' | 'fired' | 'alarms'> | null,
  /** 2213's machines have risen already (a skip, an earlier run): the alarms lead straight to the meeting. */
  machinesRisen = false,
): number | null {
  if (!click) return null;
  const slides = eveningSlides();
  const at = (i: number) => (i < 0 ? null : i);
  switch (click.kind) {
    case 'stopStep': {
      const from = stop?.step ?? null;
      const steps = stopSlides(chapter)
        .map((s) => s.step)
        .filter((step) => !(machinesRisen && step === 'machines' && from !== 'machines'));
      const to: StopStep | undefined =
        from === null || from === 'charged'
          ? 'alarms'
          : from === 'alarms' && stop!.fired < stop!.alarms
            ? 'alarms'
            : steps[steps.indexOf(from) + 1];
      return to ? at(slides.findIndex((s) => s.chapter === chapter && s.kind === 'stop' && s.step === to)) : null;
    }
    case 'wrapped':
      return slides.length - 1;
    case 'launch':
      return at(slides.findIndex((s) => s.kind === 'arrive' && s.chapter === 1));
    case 'jump':
      return at(slides.findIndex((s) => s.kind === 'arrive' && s.chapter === chapter + 1));
    case 'dock':
      return slides.length - 2;
    case 'tableGame':
      return at(slides.findIndex((s) => s.kind === 'table' && s.chapter === chapter));
    case 'endTableGame':
      return slideAfter(chapter, click.then);
    case 'phase':
      return at(slides.findIndex((s) => s.kind === 'phase' && s.chapter === chapter && s.phase === click.phase));
    case 'era':
      return at(slides.findIndex((s) => s.kind === 'phase' && s.chapter === click.chapter && s.phase === click.phase));
    default:
      return null;
  }
}

/**
 * Whether the room has reached what a click asked for, so the next press is worked out from where
 * the show really is (Marek, 2026-09-28: in the test 2213's games were skipped — a second press
 * went out before the first had landed, and stepped on past the games). `from` is where the room
 * was when the click went out.
 */
export function clickLanded(
  click: Click,
  from: { chapter: Chapter; phase: Phase; stop?: string },
  room: Pick<ShowState, 'chapter' | 'phase' | 'tableGame'> & { game: { meeting: { id: string } | null; stop?: StopRun | null } },
): boolean {
  switch (click.kind) {
    case 'phase':
      return room.chapter === from.chapter && room.phase === click.phase;
    case 'era':
      return room.chapter === click.chapter && room.phase === click.phase;
    case 'launch':
    case 'jump':
      return room.chapter > from.chapter;
    case 'dock':
      return room.phase === 'farewell';
    case 'wrapped':
      return Boolean((room as { wrappedAt?: number | null }).wrappedAt);
    case 'tableGame':
      return room.tableGame !== null && room.tableGame.chapter === from.chapter;
    case 'endTableGame':
      return click.then ? clickLanded(click.then, from, room) : room.tableGame === null;
    case 'meeting':
      return room.game.meeting !== null;
    case 'countVotes':
      return room.game.meeting === null;
    case 'stopStep':
      return from.stop === undefined || stopKey(room) !== from.stop;
    default:
      return true;
  }
}

/** A click that leaves the era: the jump on, or docking. */
export function leavesEra(click: Click | null): boolean {
  if (!click) return false;
  if (click.kind === 'endTableGame') return leavesEra(click.then);
  return click.kind === 'jump' || click.kind === 'dock';
}

/**
 * What leaving this era now would skip, in the captain's words, or null when its games are done (or
 * it has none). The clicker then needs a second press, and the consoles' jump squares say it before
 * they jump (Marek, 2026-09-28: "games must never be skipped"). Reads only what the room broadcasts;
 * a room too old to say anything about the games is never held up.
 */
export function skippedGames(state: Partial<ChargeState> & Pick<ShowState, 'chapter' | 'phase'>): string | null {
  if (state.chapter < 1 || state.chapter > 5 || state.phase === 'farewell') return null;
  if (!state.seats || !state.tasks || !state.game) return null;
  const year = chapterSpec(state.chapter).era;
  const opened = state.phase === 'fieldwork' || state.game.round?.chapter === state.chapter;
  const progress = gamesProgress(state as ChargeState);
  if (progress.total === 0) return null;
  if (!opened) return `The ${year} games have not been played yet.`;
  if (progress.done < progress.total) return `The ${year} games are not all done (${progress.done} of ${progress.total}).`;
  return null;
}
